/**
 * CCTP reverse-quote math (enter RECEIVE → compute SEND).
 *
 * The dual-cap CCTP fee (#28 / #60) charged on a bridge is:
 *
 *     fee(source) = max(platformMinFeeUsd, source * bps / 10000) + networkFee
 *
 * i.e. a percentage of the source that is FLOORED at a flat ~$0.10, plus the per-corridor network fee.
 * It is NOT a flat bps. A pure percentage inverse — `source = (dest + networkFee) / (1 - bps/10000)` —
 * therefore UNDER-sizes the source for small amounts, where the $0.10 floor dwarfs the tiny %-fee: type
 * a 1.00 receive and it sizes the send at ~1.02, but the real fee is $0.10, so the user actually receives
 * ~0.92 — less than they asked for.
 *
 * To guarantee the received amount always clears the target, size the source to whichever fee model
 * DOMINATES at that amount:
 *   • large amounts → the %-fee dominates → `(dest + networkFee) / (1 - bps/10000)`
 *   • small amounts → the $0.10 floor dominates → `dest + platformMinFeeUsd + networkFee`
 * The maximum of the two is the correct source in both regimes (and at the crossover the two agree).
 *
 * NOTE: this intentionally does NOT model Circle's fast-transfer maxFee pass-through (~2 bps, deducted
 * from the minted amount) — that value isn't available on the lightweight /estimate response and is a
 * separate, pre-existing reverse-vs-forward gap. Callers that need it must add it to `networkFee`.
 *
 * @param destAmount        Desired receive amount (USDC, human units). Must be finite & > 0.
 * @param networkFee        Per-corridor network fee in USDC (feeEstimate.networkFeeUsdc), default 0.
 * @param opts.bps          CCTP base percentage fee in bps (default 12 ≈ 0.12%). Kept conservative
 *                          (slightly above the ~10 bps base) so the source rounds toward "enough".
 * @param opts.platformMinFeeUsd  The flat platform fee floor in USD (default 0.10, #28).
 * @returns The raw source (send) amount in USDC before any per-token ceil/clamp. NaN-safe: returns 0
 *          for a non-positive or non-finite destAmount.
 */
export function cctpReverseSource(
  destAmount: number,
  networkFee = 0,
  opts: { bps?: number; platformMinFeeUsd?: number } = {}
): number {
  if (!Number.isFinite(destAmount) || destAmount <= 0) return 0
  const bps = opts.bps ?? 12
  const platformMinFeeUsd = opts.platformMinFeeUsd ?? 0.1
  const nf = Number.isFinite(networkFee) ? networkFee : 0
  // %-dominant (larger amounts): invert  source*(1 - bps/10000) - nf = dest
  const sourcePct = ((destAmount + nf) * 10000) / (10000 - bps)
  // floor-dominant (small amounts):       source - platformMinFeeUsd - nf = dest
  const sourceFloor = destAmount + platformMinFeeUsd + nf
  return Math.max(sourcePct, sourceFloor)
}
